import React, { useEffect, useState, useCallback } from "react";
import { getKpi, getTargets, setTargets, getKpiReport } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import { ChevronLeft, ChevronRight, Target, Settings2, TrendingUp, Download } from "lucide-react";
import { toast } from "sonner";

export default function Kpi() {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [targets, setTargetsState] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => {
    getKpi(offset).then(setData).catch(() => {});
  }, [offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getTargets().then(setTargetsState).catch(() => {}); }, []);

  const saveTargets = async () => {
    try {
      await setTargets(targets);
      toast.success("Obiettivi aggiornati");
      setEditOpen(false);
      load();
    } catch { toast.error("Errore salvataggio obiettivi"); }
  };

  const downloadReport = async () => {
    try {
      const res = await getKpiReport(offset);
      const blob = new Blob([res.text], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = res.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report scaricato");
    } catch { toast.error("Errore nel download del report"); }
  };

  if (!data) return <div className="p-8 text-muted-foreground">Caricamento...</div>;

  const weekLabel = offset === 0 ? "Questa settimana" : offset === -1 ? "Settimana scorsa" : `${data.week_start} – ${data.week_end}`;

  return (
    <div className="fade-in space-y-8 p-6 md:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">13 · KPI del Commerciale</div>
          <h1 className="mt-1 font-heading text-4xl font-bold tracking-tight">KPI Settimanali</h1>
          <p className="mt-1 text-sm text-muted-foreground">20% del tempo per trovare · 80% per convertire — obiettivi vs risultati reali.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadReport} data-testid="download-report-btn"><Download className="mr-2 h-4 w-4" /> Scarica report</Button>
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="edit-targets-btn"><Settings2 className="mr-2 h-4 w-4" /> Obiettivi</Button>
            </DialogTrigger>
            <DialogContent className="bg-card">
              <DialogHeader><DialogTitle className="font-heading">Obiettivi settimanali</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">Imposta gli obiettivi settimanali del commerciale.</DialogDescription>
              {targets && (
                <div className="grid grid-cols-2 gap-4 py-2">
                  {[["prospect", "Prospect trovati"], ["telefonate", "Telefonate"], ["appuntamenti", "Appuntamenti"], ["proposte", "Proposte"], ["clienti", "Nuovi clienti"]].map(([k, l]) => (
                    <div key={k} className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">{l}</Label>
                      <Input type="number" min={0} value={targets[k]} onChange={(e) => setTargetsState((t) => ({ ...t, [k]: Number(e.target.value) }))} data-testid={`target-${k}`} />
                    </div>
                  ))}
                </div>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setEditOpen(false)}>Annulla</Button>
                <Button onClick={saveTargets} data-testid="save-targets-btn">Salva</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button size="icon" variant="outline" onClick={() => setOffset((o) => o - 1)} data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <div className="min-w-52 text-center">
          <div className="font-heading text-lg font-semibold">{weekLabel}</div>
          <div className="text-xs text-muted-foreground">{data.week_start} → {data.week_end}</div>
        </div>
        <Button size="icon" variant="outline" onClick={() => setOffset((o) => Math.min(0, o + 1))} disabled={offset >= 0} data-testid="week-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {data.metrics.map((m) => {
          const pct = Math.min(100, m.pct);
          const reached = m.actual >= m.target && m.target > 0;
          const color = reached ? "#22C55E" : pct >= 60 ? "#0EA5E9" : pct >= 30 ? "#EAB308" : "#EF4444";
          return (
            <div key={m.key} className="card-lift rounded-xl border border-border bg-card p-5" data-testid={`kpi-metric-${m.key}`}>
              <div className="flex items-center justify-between">
                <span className="label-caps text-muted-foreground">{m.label}</span>
                <Target className="h-4 w-4" style={{ color }} />
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-heading text-4xl font-bold tabular-nums" style={{ color }}>{m.actual}</span>
                <span className="text-lg text-muted-foreground tabular-nums">/ {m.target}</span>
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded bg-secondary">
                <div className="h-full rounded transition-[width] duration-500" style={{ width: `${pct}%`, background: color }} />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{m.pct}% dell'obiettivo</span>
                {reached && <span className="inline-flex items-center gap-1 text-[#22C55E]"><TrendingUp className="h-3 w-3" /> Raggiunto</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
